<template>
  <div v-loading="loading" class="employee-form">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4 class="m-0">{{ title }}</h4>
      <el-button @click="$router.push({ name: 'employees-list' })">Back</el-button>
    </div>

    <p v-if="!canManageEmployees" class="text-muted">View only — you do not have permission to manage employees.</p>
    <card>
      <h5 class="section-title">Person</h5>
      <div class="row">
        <div class="col-md-2 text-center">
          <div :class="['card-avatar', { clickable: !creating && canManageEmployees }]" @click="!creating && canManageEmployees && (avatarDialogVisible = true)">
            <el-avatar v-if="user && user.avatar" :src="user.avatar" :size="96" shape="circle" />
            <el-avatar v-else :size="96" shape="circle" icon="el-icon-user" />
          </div>
          <small v-if="creating" class="d-block text-muted mt-2">The photo is added after the employee is created.</small>
          <el-button v-else-if="canManageEmployees" class="mt-2" size="mini" icon="el-icon-camera" @click="avatarDialogVisible = true">Change photo</el-button>
        </div>
        <div class="col-md-10">
          <div class="row">
            <!-- users.firstName / lastName are the English name; RU and UA are translations -->
            <form-field class="col-md-3" label="First name (EN)" :error="nameError('firstName')" required>
              <el-input :disabled="!canManageEmployees" v-model="names.firstName" placeholder="Latin letters" />
            </form-field>
            <form-field class="col-md-3" label="Last name (EN)" :error="nameError('lastName')">
              <el-input :disabled="!canManageEmployees" v-model="names.lastName" placeholder="Latin letters" />
            </form-field>
            <form-field class="col-md-3" label="Full name RU" :error="errorOf('user.metadata.full_name.ru')" required>
              <el-input :disabled="!canManageEmployees" v-model="names.ru" />
            </form-field>
            <form-field class="col-md-3" label="Full name UA" :error="errorOf('user.metadata.full_name.ua')">
              <el-input :disabled="!canManageEmployees" v-model="names.ua" />
            </form-field>
            <form-field class="col-md-4" label="Login" :error="errorOf('admins.login')" required>
              <el-input :disabled="!canManageEmployees" v-model="admins.login" />
            </form-field>
            <form-field class="col-md-3" label="Gender" :error="errorOf('admins.gender')">
              <el-radio-group :disabled="!canManageEmployees" v-model="admins.gender">
                <el-radio :label="1">M</el-radio>
                <el-radio :label="0">F</el-radio>
              </el-radio-group>
            </form-field>
            <form-field class="col-md-3" label="Birthday" :error="errorOf('admins.birthday')">
              <el-date-picker :disabled="!canManageEmployees" v-model="admins.birthday" type="date" value-format="yyyy-MM-dd" style="width: 100%" />
            </form-field>
          </div>
        </div>
      </div>

      <h5 class="section-title">Contacts</h5>
      <div class="row">
        <form-field class="col-md-3" label="Email" :error="errorOf('admins.email', 'user.email')" required>
          <el-input :disabled="!canManageEmployees" v-model="admins.email" />
        </form-field>
        <!-- the password the login checks; on edit, empty = keep the current one -->
        <form-field
          class="col-md-3"
          :label="creating ? 'Password' : 'New password'"
          :error="passwordError || errorOf('password')"
          :required="creating"
        >
          <el-input :disabled="!canManageEmployees"
            v-model="password"
            :type="passwordVisible ? 'text' : 'password'"
            :placeholder="creating ? '' : 'Leave empty to keep'"
            autocomplete="new-password"
            @input="passwordVisible = false"
          >
            <el-button v-if="canManageEmployees" slot="append" icon="el-icon-refresh" title="Generate 8 characters" @click="generatePassword" />
          </el-input>
        </form-field>
        <form-field class="col-md-3" label="Personal phone" :error="errorOf('admins.phone_private', 'user.phone')">
          <el-input :disabled="!canManageEmployees" v-model="admins.phone_private" placeholder="+380…" />
        </form-field>
        <form-field class="col-md-3" label="Telegram" :error="errorOf('admins.skype')">
          <el-input :disabled="!canManageEmployees" v-model="admins.skype" />
        </form-field>
      </div>

      <h5 class="section-title">Work</h5>
      <div class="row">
        <form-field class="col-md-3" label="Department" :error="errorOf('admins.department')" required>
          <el-select :disabled="!canManageEmployees" v-model="admins.department" filterable style="width: 100%">
            <el-option v-for="d in dictionaries.departments" :key="d.id" :label="d.name" :value="d.id" />
          </el-select>
        </form-field>
        <form-field class="col-md-3" label="Team" :error="errorOf('admins.manager_group')">
          <el-select :disabled="!canManageEmployees" v-model="admins.manager_group" filterable clearable style="width: 100%">
            <el-option v-for="g in dictionaries.manager_groups" :key="g.id" :label="g.name" :value="g.id" />
          </el-select>
        </form-field>
        <form-field class="col-md-3" label="Role on the team page" :error="errorOf('admins.role')">
          <el-select :disabled="!canManageEmployees" v-model="admins.role" style="width: 100%">
            <el-option v-for="r in roles" :key="r.value" :label="r.label" :value="r.value" />
          </el-select>
        </form-field>
        <form-field class="col-md-3" label="Start of work" :error="errorOf('admins.start_work_date')">
          <el-date-picker :disabled="!canManageEmployees" v-model="admins.start_work_date" type="date" value-format="yyyy-MM-dd" style="width: 100%" />
        </form-field>
        <form-field class="col-md-3" label="Last salary" :error="errorOf('admins.last_salary_date')">
          <el-date-picker :disabled="!canManageEmployees" v-model="admins.last_salary_date" type="date" value-format="yyyy-MM-dd" style="width: 100%" />
        </form-field>
        <form-field class="col-md-2" label="Schedule colour" :error="errorOf('admins.color')">
          <el-color-picker :disabled="!canManageEmployees" v-model="admins.color" />
        </form-field>
        <form-field class="col-md-4" label="Languages the employee speaks" :error="errorOf('admins.supported_languages')">
          <el-select :disabled="!canManageEmployees" v-model="admins.supported_languages" multiple style="width: 100%">
            <el-option v-for="l in languages" :key="l" :label="l.toUpperCase()" :value="l" />
          </el-select>
        </form-field>
      </div>
      <div class="d-flex flex-wrap flags">
        <el-checkbox :disabled="!canManageEmployees" v-model="admins.fired">Fired</el-checkbox>
        <el-checkbox :disabled="!canManageEmployees" v-model="admins.on_flex">On vacation</el-checkbox>
        <el-checkbox :disabled="!canManageEmployees" v-model="admins.hide">Hidden from manager pickers</el-checkbox>
        <el-checkbox :disabled="!canManageEmployees" v-model="admins.supervisor">Supervisor</el-checkbox>
      </div>

      <h5 class="section-title">Telephony and chat</h5>
      <div class="row">
        <form-field class="col-md-3" label="Streamtele number" :error="errorOf('admins.line')">
          <el-input :disabled="!canManageEmployees" v-model="admins.line" />
        </form-field>
        <form-field class="col-md-5" label="Ringostat user" :error="errorOf('admins.sipid')">
          <el-input :disabled="!canManageEmployees" v-model="admins.sipid" />
        </form-field>
        <form-field class="col-md-4" label="Chat2Desk / novachat id" :error="errorOf('admins.public_id')">
          <el-input :disabled="!canManageEmployees" v-model="admins.public_id" />
        </form-field>
      </div>

      <h5 class="section-title">Access</h5>
      <div class="d-flex flex-wrap flags">
        <el-checkbox :disabled="!canManageEmployees" v-model="admins.work_with_agencies">Works with agencies</el-checkbox>
        <el-checkbox :disabled="!canManageEmployees" v-model="admins.work_with_reviews">Review moderator</el-checkbox>
        <el-checkbox :disabled="!canManageEmployees" v-model="admins.allowed_edit_price">May edit prices</el-checkbox>
      </div>

    </card>

    <card>
      <h5 class="section-title mt-0">Public profile (site)</h5>
      <employee-profile-content v-model="content" :read-only="!canManageEmployees" />
      <div class="row">
        <form-field class="col-md-6" label="Video presentation (URL)" :error="errorOf('user.metadata.video_presentation')">
          <el-input :disabled="!canManageEmployees" v-model="metadata.video_presentation" />
        </form-field>
        <form-field class="col-md-6" label="Social networks" :error="errorOf('user.metadata.social_networks')">
          <url-list :read-only="!canManageEmployees" v-model="metadata.social_networks" placeholder="https://www.linkedin.com/in/…" />
        </form-field>
      </div>
    </card>

    <card>
      <h5 class="section-title mt-0">Qualification</h5>
      <div class="row">
        <form-field class="col-md-3" label="Diploma number" :error="errorOf('user.metadata.diploma_number')">
          <el-input :disabled="!canManageEmployees" v-model="metadata.diploma_number" />
        </form-field>
        <form-field class="col-md-3" label="Certificate number" :error="errorOf('user.metadata.cert_number')">
          <el-input :disabled="!canManageEmployees" v-model="metadata.cert_number" />
        </form-field>
        <form-field class="col-md-3" label="ORCID" :error="errorOf('admins.orcid_number')">
          <el-input :disabled="!canManageEmployees" v-model="admins.orcid_number" />
        </form-field>
        <form-field class="col-md-3" label="Specialities" :error="errorOf('admins.specialities')">
          <el-select :disabled="!canManageEmployees" v-model="admins.specialities" multiple filterable allow-create default-first-option style="width: 100%" />
        </form-field>
        <form-field class="col-md-12" label="Certificates (image URLs)" :error="errorOf('user.metadata.certificates')">
          <url-list :read-only="!canManageEmployees" v-model="metadata.certificates" placeholder="https://img.bookimed.com/…" />
        </form-field>
      </div>
    </card>

    <employee-avatar-dialog
      v-if="!creating"
      :visible.sync="avatarDialogVisible"
      :user-id="id"
      :name="title"
      :current="user ? user.avatar : null"
      @changed="user = { ...user, avatar: $event }"
    />

    <!-- grey until something on the page is edited -->
    <el-button
      v-if="canManageEmployees"
      class="floating-save"
      type="primary"
      icon="el-icon-check"
      :disabled="!dirty"
      :loading="saving"
      @click="save"
    >{{ creating ? "Create" : "Save" }}</el-button>
  </div>
</template>

<script>
import employeeAccess from "@/components/Employees/employeeAccess";
import APIEmployees from "@/api/modules/employees";
import { resultErrorMessage, resultErrorOf } from "@/api/helpers/resultError";
import EmployeeProfileContent, { PROFILE_FIELDS } from "@/components/Employees/EmployeeProfileContent";
import { EMPLOYEE_LANGUAGES, normaliseTranslations } from "@/components/Employees/employeeLanguages";
import { PASSWORD_MAX_BYTES, PASSWORD_MIN_LENGTH, randomPassword } from "./password";
import EmployeeAvatarDialog from "./EmployeeAvatarDialog";

// AdminsRecord::ADMIN_ROLES
const ROLES = [
  { value: "", label: "Not shown on the team page" },
  { value: "coordinator", label: "Coordinator" },
  { value: "editor", label: "Editor" },
  { value: "creator", label: "Content creator" },
  { value: "founder", label: "Founder" },
];

// the profile's `admins` keys this form edits (users returns them all, typed)
const ADMINS_KEYS = [
  "email", "phone_private", "login", "fired", "on_flex", "hide", "supervisor",
  "department", "manager_group", "line", "sipid", "public_id", "work_with_agencies",
  "work_with_reviews", "allowed_edit_price", "role", "supported_languages", "birthday", "start_work_date",
  "last_salary_date", "gender", "color", "skype", "orcid_number", "specialities",
];
// {id, name} in the profile, a plain id in the PATCH
const REFERENCE_KEYS = ["department", "manager_group"];
const METADATA_KEYS = ["video_presentation", "social_networks", "certificates", "diploma_number", "cert_number"];

const emptyAdmins = () => ({
  email: "", phone_private: "", login: "", fired: false, on_flex: false,
  hide: false, supervisor: false, department: null, manager_group: null, line: "",
  sipid: "", public_id: "", work_with_agencies: false, work_with_reviews: false, allowed_edit_price: false,
  role: "", supported_languages: [], birthday: null, start_work_date: null, last_salary_date: null, gender: 1,
  color: "#ff3333", skype: "", orcid_number: "", specialities: [],
});
// the name block; firstName/lastName go to users as they are, RU/UA to metadata.full_name
const emptyNames = () => ({ firstName: "", lastName: "", ru: "", ua: "" });
const CYRILLIC = /[\u0400-\u04FF]/;
// profile -> the name block: users holds the English name, admins the real RU / UA (metadata shows fallbacks)
const namesFromProfile = (admins, user) => ({
  firstName: (user && user.firstName) || "",
  lastName: (user && user.lastName) || "",
  ru: ((admins && admins.full_name) || {}).ru || "",
  ua: ((admins && admins.full_name) || {}).ua || "",
});
const emptyContent = () => PROFILE_FIELDS.reduce((c, { key }) => ({ ...c, [key]: normaliseTranslations({}) }), {});
const emptyMetadata = () => ({ video_presentation: "", social_networks: [], certificates: [], diploma_number: "", cert_number: "" });

const clone = (value) => JSON.parse(JSON.stringify(value));
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);

// profile `admins` (nulls for empty, {id, name} for references) -> the form's shape
const adminsFromProfile = (profile) => {
  const admins = emptyAdmins();
  ADMINS_KEYS.forEach((key) => {
    const value = profile[key];
    if (REFERENCE_KEYS.includes(key)) {
      admins[key] = value ? value.id : null;
    } else if (value !== null && value !== undefined) {
      admins[key] = value;
    }
  });

  return admins;
};

// A small inline list editor for URL arrays (social networks, certificates).
const UrlList = {
  name: "UrlList",
  props: { readOnly: { type: Boolean, default: false }, value: { type: Array, default: () => [] }, placeholder: { type: String, default: "" } },
  render(h) {
    const rows = this.value.map((url, index) =>
      h("div", { class: "d-flex mb-1", key: index }, [
        h("el-input", {
          props: { disabled: this.readOnly, value: url, placeholder: this.placeholder, size: "small" },
          on: { input: (v) => !this.readOnly && this.$emit("input", this.value.map((u, i) => (i === index ? v : u))) },
        }),
        h("el-button", {
          class: "ml-1",
          props: { disabled: this.readOnly, icon: "el-icon-delete", size: "small" },
          on: { click: () => !this.readOnly && this.$emit("input", this.value.filter((u, i) => i !== index)) },
        }),
      ])
    );

    return h("div", [
      ...rows,
      h("el-button", { props: { disabled: this.readOnly, size: "mini", icon: "el-icon-plus" }, on: { click: () => !this.readOnly && this.$emit("input", [...this.value, ""]) } }, "Add"),
    ]);
  },
};

// label + control + the users service's error for that field
const FormField = {
  name: "FormField",
  props: { label: String, error: String, required: Boolean },
  render(h) {
    return h("div", { class: ["form-group", { "has-error": !!this.error }] }, [
      h("label", { class: "field-label" }, [this.label, this.required ? h("span", { class: "text-danger" }, " *") : null]),
      this.$slots.default,
      this.error ? h("small", { class: "text-danger d-block" }, this.error) : null,
    ]);
  },
};

// users' field reasons, for an editor
const REASONS = {
  required: "Required",
  invalid_type: "Wrong type",
  invalid_email: "Not an email address",
  invalid_phone: "Not a phone number",
  invalid_date: "Not a date",
  invalid_language: "Unknown language",
  invalid_value: "Not allowed",
  too_long: "Too long",
  unknown_id: "No longer exists",
  taken: "Already used by someone else",
  read_only: "Cannot be changed",
  not_latin: "Latin letters only",
  conflicts_with_admins: "Contradicts the employee's data",
  too_short: `At least ${PASSWORD_MIN_LENGTH} characters`,
};

export default {
  mixins: [employeeAccess],
  name: "EmployeeEdit",
  components: { EmployeeProfileContent, UrlList, FormField, EmployeeAvatarDialog },
  props: { id: { type: [String, Number], default: null } },
  data() {
    return {
      loading: false,
      saving: false,
      admins: emptyAdmins(),
      content: emptyContent(),
      metadata: emptyMetadata(),
      names: emptyNames(),
      user: null,
      original: null,
      dictionaries: { departments: [], manager_groups: [] },
      fieldErrors: {},
      password: "",
      passwordVisible: false,
      passwordError: "",
      // set by any edit after the data is on the page; no diffing
      dirty: false,
      avatarDialogVisible: false,
      watching: false,
    };
  },
  computed: {
    creating() {
      return !this.id;
    },
    title() {
      if (this.creating) return "New employee";

      const english = `${this.names.firstName} ${this.names.lastName}`.trim();

      return this.names.ru || english || `Employee #${this.id}`;
    },
    roles() {
      return ROLES;
    },
    languages() {
      return EMPLOYEE_LANGUAGES;
    },
  },
  methods: {
    async load() {
      await this.$store.dispatch("employees/refreshPermissions");
      this.loading = true;
      try {
        const dictionaries = await APIEmployees.getDictionaries();
        if (this.refused(dictionaries)) return;
        this.dictionaries = dictionaries.data;

        if (this.creating) {
          this.remember();

          return;
        }

        const response = await APIEmployees.getProfile(this.id);
        if (this.refused(response)) return;
        const { admins, user } = response.data.employee;
        this.user = user;
        this.admins = adminsFromProfile(admins);
        this.names = namesFromProfile(admins, user);
        const stored = (user && user.metadata) || {};
        this.content = PROFILE_FIELDS.reduce((c, { key }) => ({ ...c, [key]: normaliseTranslations(stored[key]) }), {});
        const defaults = emptyMetadata();
        this.metadata = METADATA_KEYS.reduce((m, key) => {
          const value = stored[key] === undefined || stored[key] === null ? defaults[key] : stored[key];

          return { ...m, [key]: value };
        }, {});
        this.remember();
      } catch (e) {
        this.notifyError(e, "Could not load the employee");
      } finally {
        this.loading = false;
      }
    },
    remember() {
      this.original = clone({ admins: this.admins, content: this.content, metadata: this.metadata, names: this.names });
      // the watchers below fire for the assignment that just happened: start listening after it
      this.watching = false;
      this.$nextTick(() => {
        this.dirty = false;
        this.watching = true;
      });
    },
    touched() {
      if (this.watching) this.dirty = true;
    },
    /** Only what the editor changed: the service validates and writes each key separately. */
    changes() {
      const admins = {};
      ADMINS_KEYS.forEach((key) => {
        if (!same(this.admins[key], this.original.admins[key])) admins[key] = this.admins[key];
      });
      if (admins.phone_private !== undefined) admins.phone_private = (admins.phone_private || "").trim();

      const metadata = {};
      Object.keys(this.content).forEach((key) => {
        if (!same(this.content[key], this.original.content[key])) metadata[key] = this.content[key];
      });
      METADATA_KEYS.forEach((key) => {
        if (!same(this.metadata[key], this.original.metadata[key])) metadata[key] = this.metadata[key];
      });
      ["social_networks", "certificates"].forEach((key) => {
        if (metadata[key]) metadata[key] = metadata[key].map((u) => u.trim()).filter(Boolean);
      });

      const user = {};
      ["firstName", "lastName"].forEach((key) => {
        const value = this.names[key].trim();
        if (this.creating || value !== this.original.names[key]) user[key] = value;
      });
      const fullName = {};
      ["ru", "ua"].forEach((lang) => {
        const value = this.names[lang].trim();
        if (this.creating || value !== this.original.names[lang]) fullName[lang] = value;
      });
      if (Object.keys(fullName).length) metadata.full_name = fullName;

      const body = {};
      if (Object.keys(admins).length) body.admins = admins;
      if (Object.keys(metadata).length) user.metadata = metadata;
      if (Object.keys(user).length) body.user = user;

      return body;
    },
    async save() {
      if (!this.canManageEmployees || !(await this.ensureEmployeeWriteAccess())) return;
      this.fieldErrors = {};
      this.passwordError = "";
      const latin = ["firstName", "lastName"].filter((key) => CYRILLIC.test(this.names[key]));
      if (latin.length) {
        this.fieldErrors = latin.reduce((e, key) => ({ ...e, [`user.${key}`]: "not_latin" }), {});

        return;
      }
      const body = this.changes();

      const password = this.password;
      if ((this.creating || password) && !this.validPassword(password)) {
        this.passwordError = `${PASSWORD_MIN_LENGTH} to ${PASSWORD_MAX_BYTES} characters`;

        return;
      }
      if (this.creating) {
        body.password = password;
      } else if (!Object.keys(body).length && !password) {
        this.$notify({ type: "info", title: "Nothing changed" });

        return;
      }

      this.saving = true;
      try {
        if (!this.creating && Object.keys(body).length) {
          const saved = await APIEmployees.updateProfile(this.id, body);
          if (this.refused(saved)) return;
        }
        if (!this.creating && password) {
          const changed = await APIEmployees.setPassword(this.id, password);
          if (this.refused(changed)) return;
          this.password = "";
        }
        const created = this.creating ? await APIEmployees.create(body) : null;
        if (created && this.refused(created)) return;
        this.$notify({ type: "success", title: this.creating ? "Employee created" : "Saved" });
        if (this.creating) {
          const { id } = created.data.employee;
          this.$router.replace({ name: "employees-edit", params: { id: String(id) } });

          return;
        }
        await this.load();
      } catch (e) {
        this.notifyError(e, "Could not save");
      } finally {
        this.saving = false;
      }
    },
    validPassword(password) {
      return password.length >= PASSWORD_MIN_LENGTH && new TextEncoder().encode(password).length <= PASSWORD_MAX_BYTES;
    },
    generatePassword() {
      if (!this.canManageEmployees) return;
      this.password = randomPassword();
      this.$nextTick(() => {
        this.passwordVisible = true;
      });
    },
    nameError(key) {
      return this.errorOf(`user.${key}`);
    },
    errorOf(...keys) {
      const hit = Object.keys(this.fieldErrors).find((field) => keys.some((key) => field === key || field.startsWith(`${key}.`)));
      if (!hit) return "";

      return REASONS[this.fieldErrors[hit]] || this.fieldErrors[hit];
    },
    // cms-api answers a refusal with HTTP 200 and a Result body
    refused(response) {
      const error = resultErrorOf(response);
      if (!error) return false;

      if (error.code === "users.employee.invalid_profile" || error.code === "users.employee.invalid_password") {
        const fields = (error.data && error.data.fields) || {};
        if (error.data && error.data.field) fields[error.data.field] = error.data.reason;
        this.fieldErrors = fields;
        // the public-profile fields are not highlighted one by one: list every refusal here too
        const message = Object.entries(fields)
          .map(([field, reason]) => `${field.replace(/^(admins|user\.metadata|user)\./, "")}: ${REASONS[reason] || reason}`)
          .join("; ");
        this.$notify({ type: "warning", title: "Check the highlighted fields", message });

        return true;
      }
      this.$notify({ type: "warning", title: resultErrorMessage(error) });

      return true;
    },
    notifyError(error, fallback) {
      const payload = error && error.response && error.response.data;
      if (payload && payload.error) {
        this.$notify({ type: "warning", title: resultErrorMessage(payload.error) });

        return;
      }
      this.$notify({ type: "warning", title: (error && error.message) || fallback });
    },
  },
  watch: {
    id() {
      this.load();
    },
    admins: { deep: true, handler: "touched" },
    content: { deep: true, handler: "touched" },
    metadata: { deep: true, handler: "touched" },
    names: { deep: true, handler: "touched" },
    password: "touched",
  },
  created() {
    this.load();
  },
};
</script>

<style scoped>
.floating-save {
  position: fixed;
  right: 32px;
  bottom: 32px;
  z-index: 2000;
  padding: 14px 28px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.card-avatar.clickable {
  cursor: pointer;
  display: inline-block;
}

.section-title {
  margin: 24px 0 12px;
  font-weight: 600;
}


.flags > * {
  margin-right: 24px;
  margin-bottom: 8px;
}

.has-error ::v-deep .el-input__inner {
  border-color: #f56c6c;
}
</style>

<style>
/* not scoped: FormField renders these, and scoped styles do not reach a render-function child */
.employee-form .field-label {
  display: block;
  font-size: 12px;
  margin-bottom: 4px;
}

.employee-form .form-group .el-radio-group,
.employee-form .form-group .el-color-picker {
  display: flex;
  align-items: center;
  min-height: 40px;
}
</style>
